import React, { useState, useRef, useCallback } from 'react';
import { Button, Tooltip, Snackbar, Box, CircularProgress } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import XIcon from '@mui/icons-material/X';
import PopoverMenu from './PopoverMenu';
import { uploadShareThumbnail } from '../../services/contents_api';

interface GeneratedShareButtonProps {
  /** Canvas等で画像Blobを生成する関数 */
  generateImage: () => Promise<Blob | null>;
  /** OGPシェアページのベースURL（REACT_APP_FRONTEND_URL含む完全パス） */
  shareUrl: string;
  /** Xシェア時のテキスト */
  shareText: string;
  /** R2アップロード時のtype（パス分類用） */
  uploadType?: string;
  /** ダウンロード時のファイル名 */
  downloadFileName?: string;
  /** Xシェア時のハッシュタグ */
  hashtags?: string[];
  /** PopoverMenuのタイトル */
  menuTitle?: string;
}

const GeneratedShareButton: React.FC<GeneratedShareButtonProps> = ({
  generateImage,
  shareUrl,
  shareText,
  uploadType = 'favorites',
  downloadFileName = 'share-image',
  hashtags = ['PULSE'],
  menuTitle = 'オプション',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const cachedOgUrl = useRef<string | null>(null);
  const cachedBlob = useRef<Blob | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getBlob = useCallback(async (): Promise<Blob | null> => {
    if (cachedBlob.current) return cachedBlob.current;
    const blob = await generateImage();
    if (blob) cachedBlob.current = blob;
    return blob;
  }, [generateImage]);

  const ensureGenerated = useCallback(async (): Promise<string> => {
    if (cachedOgUrl.current) return cachedOgUrl.current;

    const blob = await getBlob();
    if (!blob) return shareUrl;

    try {
      const contentHash = Array.from(
        new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()))
      ).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);

      const result = await uploadShareThumbnail(blob, contentHash, uploadType);
      const ogUrl = `${shareUrl}?img=${result.content_hash}`;
      cachedOgUrl.current = ogUrl;
      return ogUrl;
    } catch (error) {
      console.error('サムネイルアップロードに失敗:', error);
      return shareUrl;
    }
  }, [getBlob, shareUrl, uploadType]);

  const handleShareX = async () => {
    handleClose();
    setLoading(true);
    try {
      const ogUrl = await ensureGenerated();
      const hashtagsParam = hashtags.length > 0 ? `&hashtags=${encodeURIComponent(hashtags.join(','))}` : '';
      const xShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(ogUrl)}${hashtagsParam}`;
      window.open(xShareUrl, '_blank');
    } catch (error) {
      setSnackbarMessage('シェアに失敗しました');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    handleClose();
    setLoading(true);
    try {
      const ogUrl = await ensureGenerated();
      await navigator.clipboard.writeText(ogUrl);
      setSnackbarMessage('URLをコピーしました');
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage('コピーに失敗しました');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    handleClose();
    setLoading(true);
    try {
      const blob = await getBlob();
      if (!blob) {
        setSnackbarMessage('画像の生成に失敗しました');
        setOpenSnackbar(true);
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      setSnackbarMessage('ダウンロードに失敗しました');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const menuOptions = [
    {
      icon: <XIcon />,
      label: 'Xでシェア',
      onClick: handleShareX,
    },
    {
      icon: <ContentCopyIcon />,
      label: 'URLをコピー',
      onClick: handleCopyLink,
    },
    {
      icon: <DownloadIcon />,
      label: '画像をダウンロード',
      onClick: handleDownload,
    },
  ];

  return (
    <>
      <Tooltip title="シェア">
        <Button
          onClick={handleClick}
          size="small"
          disabled={loading}
          sx={{
            minWidth: '32px',
            p: '4px 4px',
            fontSize: '14px',
            textTransform: 'none',
            lineHeight: '1',
            borderRadius: '4px',
            color: 'text.secondary',
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ShareIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
            )}
          </Box>
        </Button>
      </Tooltip>

      <PopoverMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        title={menuTitle}
        options={menuOptions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default GeneratedShareButton;
